// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: decisions/0031-shared-wizard-settings-editors.md
// @greenlight-spec: decisions/0040-multiple-plugin-marketplace-repositories.md

import * as React from 'react';

import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ALL_AGENT_KINDS, type AgentKind } from '@greenlight/shared';

const AGENT_DESCRIPTIONS: Record<AgentKind, { label: string; description: string }> = {
  claude: {
    label: 'Claude',
    description:
      'Covers claude.ai, Claude Desktop, Cowork and Claude Code through your organization’s plugins.',
  },
  codex: { label: 'Codex', description: 'OpenAI Codex CLI and IDE extension.' },
  cursor: { label: 'Cursor', description: 'Cursor editor plugin marketplace integration.' },
  copilot: {
    label: 'Copilot',
    description: 'GitHub Copilot CLI, VS Code, and the Copilot desktop app.',
  },
  other: {
    label: 'Other',
    description: 'The MCP-URL/CLI fallback for custom or unlisted tools.',
  },
};

const VALIDATION_ERROR = 'Select at least one coding agent.';

export interface SupportedAgentsEditorState {
  selected: AgentKind[];
  toggle: (agent: AgentKind) => void;
  error: string | null;
  isDirty: boolean;
  isValid: boolean;
  validate: () => AgentKind[] | null;
  reset: () => void;
}

function sameSet(a: AgentKind[], b: AgentKind[]): boolean {
  if (a.length !== b.length) return false;
  const bSet = new Set(b);
  return a.every((v) => bSet.has(v));
}

export function useSupportedAgentsEditor(initialValue: AgentKind[]): SupportedAgentsEditorState {
  const [selected, setSelected] = React.useState<AgentKind[]>(initialValue);
  const [validated, setValidated] = React.useState(false);
  // Settings refetches (e.g. after saving an unrelated field) hand back a new
  // array reference even when the content is unchanged — compare by content
  // so an in-progress unsaved selection survives those refetches.
  const lastInitialValue = React.useRef(initialValue);

  React.useEffect(() => {
    if (sameSet(initialValue, lastInitialValue.current)) return;
    lastInitialValue.current = initialValue;
    setSelected(initialValue);
    setValidated(false);
  }, [initialValue]);

  const currentError = selected.length === 0 ? VALIDATION_ERROR : null;

  return {
    selected,
    toggle: (agent) => {
      setSelected((prev) =>
        prev.includes(agent) ? prev.filter((a) => a !== agent) : [...prev, agent],
      );
    },
    error: validated ? currentError : null,
    isDirty: !sameSet(selected, initialValue),
    isValid: currentError === null,
    validate: () => {
      setValidated(true);
      return currentError === null ? selected : null;
    },
    reset: () => {
      setSelected(initialValue);
      setValidated(false);
    },
  };
}

export interface SupportedAgentsEditorProps {
  editor: SupportedAgentsEditorState;
  disabled?: boolean;
  description?: string;
}

export function SupportedAgentsEditor({
  editor,
  disabled = false,
  description = 'Only the selected agents appear in Agent setup and the install wizard handoff.',
}: SupportedAgentsEditorProps): React.JSX.Element {
  const groupId = React.useId();
  const errorId = `${groupId}-error`;

  return (
    <div className="flex flex-col gap-1.5" role="group" aria-describedby={errorId}>
      <div className="flex flex-col gap-3">
        {ALL_AGENT_KINDS.map((agent) => {
          const { label, description: agentDescription } = AGENT_DESCRIPTIONS[agent];
          const checkboxId = `${groupId}-${agent}`;
          return (
            <div key={agent} className="flex items-start gap-2.5">
              <Checkbox
                id={checkboxId}
                checked={editor.selected.includes(agent)}
                onCheckedChange={() => editor.toggle(agent)}
                onBlur={() => editor.validate()}
                disabled={disabled}
                className="mt-0.5"
              />
              <div className="flex flex-col gap-0.5">
                <Label htmlFor={checkboxId} className="font-medium">
                  {label}
                </Label>
                <p className="text-xs text-muted-foreground">{agentDescription}</p>
              </div>
            </div>
          );
        })}
      </div>
      {editor.error ? (
        <p id={errorId} role="alert" className="text-xs text-destructive">
          {editor.error}
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
    </div>
  );
}
